import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FaChartPie, FaChild, FaConciergeBell, FaMobileAlt, FaUtensils } from 'react-icons/fa';

import {
  fetchCateringActiveOrders,
  fetchCateringPayments,
  fetchPosOrdersWithPayments,
  fetchRestaurantOrderHistory,
  fetchRestaurantOrders,
  normalizeRestaurantPaymentsSummary,
  orderMatchesSource,
} from './orderApi';
import { getEffectiveRestaurantId, formatSettlementAmount } from './financeApi';
import { useRestaurantEnabledServices } from './useRestaurantEnabledServices';
import { filterOrderChannelsByServices } from './restaurantServiceAccess';
import { ORDER_CHANNELS } from './menuChannels';

import OrdersSortableTh from './OrdersSortableTh';
import { buildSortHandlers, parseDateSortValue, sortList } from './tableSortUtils';

import './css/Inventory.css';
import './css/Menu.css';
import './css/Purchase.css';
import './css/Receivables.css';
import './css/OrdersTablePanel.css';

/** Inclusive calendar-day span must be ≤ ~2 months. */
const MAX_SALES_RANGE_DAYS = 62;

function todayYmd() {
  return new Date().toISOString().slice(0, 10);
}

function parseYmd(ymd) {
  if (!ymd) return null;
  const d = new Date(`${ymd}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function inclusiveDaySpan(fromDate, toDate) {
  const from = parseYmd(fromDate);
  const to = parseYmd(toDate);
  if (!from || !to) return 0;
  return Math.floor((to - from) / 86400000) + 1;
}

function formatDisplayDate(ymd) {
  const d = parseYmd(ymd);
  if (!d) return ymd || '';
  try {
    return d.toLocaleDateString('en-IN', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return ymd;
  }
}

function orderDateYmd(order) {
  const raw =
    order?.createdDate
    ?? order?.createdAt
    ?? order?.Created_Date
    ?? order?.created_date
    ?? '';
  const s = String(raw).trim();
  if (!s) return '';
  if (s.includes('T')) return s.split('T')[0];
  if (s.includes(' ')) return s.split(' ')[0];
  return s.substring(0, 10);
}

function isCancelledOrder(order) {
  const status = String(
    order?.orderStatus ?? order?.OrderStatus ?? order?.order_status ?? ''
  )
    .trim()
    .toUpperCase();
  return status === 'CANCELLED' || status === 'CAN' || status === 'CANCEL';
}

function orderTotal(order) {
  const n = Number(order?.totalPrice ?? order?.TotalPrice ?? order?.total_price ?? 0);
  return Number.isFinite(n) ? n : 0;
}

function emptyDayBucket() {
  return { amounts: {}, orderCount: 0 };
}

function addOrdersToDayMap(dayMap, channelKey, orders, fromDate, toDate) {
  (Array.isArray(orders) ? orders : []).forEach((order) => {
    const d = orderDateYmd(order);
    if (!d || d < fromDate || d > toDate) return;
    if (isCancelledOrder(order)) return;
    if (!dayMap[d]) dayMap[d] = emptyDayBucket();
    const amount = orderTotal(order);
    dayMap[d].amounts[channelKey] = (dayMap[d].amounts[channelKey] || 0) + amount;
    dayMap[d].orderCount += 1;
  });
}

function addPaymentRowsToDayMap(dayMap, channelKey, paymentRows, fromDate, toDate) {
  (Array.isArray(paymentRows) ? paymentRows : []).forEach((row) => {
    const d = String(row?.date || '').substring(0, 10);
    if (!d || d < fromDate || d > toDate) return;
    if (!dayMap[d]) dayMap[d] = emptyDayBucket();
    const paymentAmount = Number(row.totalAmount) || 0;
    const orderAmount = dayMap[d].amounts[channelKey] || 0;
    dayMap[d].amounts[channelKey] = Math.max(orderAmount, paymentAmount);
    if (!dayMap[d].orderCount) {
      dayMap[d].orderCount = Number(row.totalOrders) || 0;
    }
  });
}

async function fetchOnlineOrdersForSource(restaurantId, orderSource) {
  const opts = { orderSource };
  const [active, history] = await Promise.all([
    fetchRestaurantOrders(restaurantId, opts).catch(() => []),
    fetchRestaurantOrderHistory(restaurantId, opts).catch(() => []),
  ]);
  const seen = new Set();
  const merged = [];
  for (const order of [...active, ...history]) {
    const id = order?.orderId ?? order?.Order_Id;
    const key = id != null ? String(id) : '';
    if (key && seen.has(key)) continue;
    if (!orderMatchesSource(order, orderSource)) continue;
    if (key) seen.add(key);
    merged.push(order);
  }
  return merged;
}

const CHANNEL_META = {
  offline: {
    label: 'Dine-In',
    icon: FaUtensils,
    tone: 'primary',
  },
  myeats: {
    label: 'My Eats',
    icon: FaMobileAlt,
    tone: 'warning',
  },
  catering: {
    label: 'Catering',
    icon: FaConciergeBell,
    tone: 'success',
  },
  jreats: {
    label: 'Jr Eats',
    icon: FaChild,
    tone: 'primary',
  },
};

const Sales = () => {
  const restaurantId = getEffectiveRestaurantId();
  const { enabledTypes, loading: servicesLoading } = useRestaurantEnabledServices();
  const [fromDate, setFromDate] = useState(() => todayYmd());
  const [toDate, setToDate] = useState(() => todayYmd());
  const [showDateFilters, setShowDateFilters] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rangeError, setRangeError] = useState('');
  const [amounts, setAmounts] = useState({});
  const [dailyRows, setDailyRows] = useState([]);
  const [tableSort, setTableSort] = useState({ key: 'date', direction: 'desc' });
  const { handleSortAsc, handleSortDesc } = useMemo(
    () => buildSortHandlers(setTableSort),
    []
  );

  const channels = useMemo(() => {
    const allowed = filterOrderChannelsByServices(ORDER_CHANNELS, enabledTypes, {
      servicesLoading,
      allowAllWhenUnknown: !servicesLoading && enabledTypes == null,
    });
    return allowed.filter((ch) => CHANNEL_META[ch.key]);
  }, [enabledTypes, servicesLoading]);

  const validateRange = useCallback((from, to) => {
    if (!from || !to) {
      return 'Select both From and To dates.';
    }
    if (from > to) {
      return 'From date cannot be after To date.';
    }
    if (inclusiveDaySpan(from, to) > MAX_SALES_RANGE_DAYS) {
      return 'Date range cannot exceed 2 months.';
    }
    return '';
  }, []);

  const loadSales = useCallback(async () => {
    if (!restaurantId) {
      setError('Select or sign in to a restaurant first.');
      setAmounts({});
      setDailyRows([]);
      return;
    }
    if (servicesLoading) return;

    const invalid = validateRange(fromDate, toDate);
    setRangeError(invalid);
    if (invalid) {
      setAmounts({});
      setDailyRows([]);
      return;
    }

    const keys = channels.map((ch) => ch.key);
    if (!keys.length) {
      setAmounts({});
      setDailyRows([]);
      setError('');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const dayMap = {};

      await Promise.all(
        keys.map(async (key) => {
          if (key === 'offline') {
            const orders = await fetchPosOrdersWithPayments(restaurantId).catch(() => []);
            addOrdersToDayMap(dayMap, key, orders, fromDate, toDate);
            return;
          }
          if (key === 'myeats') {
            const orders = await fetchOnlineOrdersForSource(restaurantId, 'MYEATS');
            addOrdersToDayMap(dayMap, key, orders, fromDate, toDate);
            return;
          }
          if (key === 'jreats') {
            const orders = await fetchOnlineOrdersForSource(restaurantId, 'JREATS');
            addOrdersToDayMap(dayMap, key, orders, fromDate, toDate);
            return;
          }
          if (key === 'catering') {
            const [orders, paymentsData] = await Promise.all([
              fetchCateringActiveOrders(restaurantId).catch(() => []),
              fetchCateringPayments(restaurantId).catch(() => ({})),
            ]);
            addOrdersToDayMap(dayMap, key, orders, fromDate, toDate);
            const paymentRows = normalizeRestaurantPaymentsSummary(paymentsData);
            addPaymentRowsToDayMap(dayMap, key, paymentRows, fromDate, toDate);
          }
        })
      );

      const nextAmounts = {};
      keys.forEach((key) => {
        nextAmounts[key] = 0;
      });

      const rows = Object.keys(dayMap)
        .sort((a, b) => (a < b ? 1 : a > b ? -1 : 0))
        .map((date) => {
          const bucket = dayMap[date];
          const channelAmounts = {};
          let rowTotal = 0;
          keys.forEach((key) => {
            const value = Number(bucket.amounts[key]) || 0;
            channelAmounts[key] = value;
            nextAmounts[key] += value;
            rowTotal += value;
          });
          return {
            date,
            amounts: channelAmounts,
            total: rowTotal,
            orderCount: bucket.orderCount || 0,
          };
        });

      setAmounts(nextAmounts);
      setDailyRows(rows);
    } catch (err) {
      console.error('Failed to load sales amounts:', err?.response?.data ?? err);
      setError(err?.response?.data?.error || err?.message || 'Could not load sales amounts.');
      setDailyRows([]);
    } finally {
      setLoading(false);
    }
  }, [restaurantId, channels, servicesLoading, fromDate, toDate, validateRange]);

  useEffect(() => {
    loadSales();
  }, [loadSales]);

  const isTodayOnly = fromDate === toDate && fromDate === todayYmd();
  const showTodaysInSection = !showDateFilters && isTodayOnly;

  const rangeLabel = useMemo(() => {
    if (fromDate === toDate) return formatDisplayDate(fromDate);
    return `${formatDisplayDate(fromDate)} → ${formatDisplayDate(toDate)}`;
  }, [fromDate, toDate]);

  const totalAmount = useMemo(
    () => channels.reduce((sum, ch) => sum + (Number(amounts[ch.key]) || 0), 0),
    [channels, amounts]
  );

  const sortedDailyRows = useMemo(() => {
    const getSortValue = (row, key) => {
      if (key === 'date') return parseDateSortValue(row.date);
      if (key === 'total') return Number(row.total) || 0;
      if (key === 'orderCount') return Number(row.orderCount) || 0;
      if (key.startsWith('channel:')) {
        const channelKey = key.slice('channel:'.length);
        return Number(row.amounts?.[channelKey]) || 0;
      }
      return 0;
    };
    return sortList(dailyRows, tableSort, getSortValue);
  }, [dailyRows, tableSort]);

  const handleFromChange = (value) => {
    setFromDate(value);
    setRangeError(validateRange(value, toDate));
  };

  const handleToChange = (value) => {
    setToDate(value);
    setRangeError(validateRange(fromDate, value));
  };

  const tableColSpan = channels.length + 3;

  return (
    <div className="menu inventory-page purchase-page receivables-page sales-page">
      <div className="sales-header">
        <div className="sales-header__title-block">
          {!showTodaysInSection && (
            <>
              <h2 className="sales-heading">Sales</h2>
              <p className="sales-subheading">{rangeLabel}</p>
            </>
          )}
        </div>
        <div className="sales-header__actions">
          <button
            type="button"
            className="purchase-btn purchase-btn--secondary"
            onClick={() => setShowDateFilters((open) => !open)}
            aria-expanded={showDateFilters}
          >
            {showDateFilters ? 'Hide Date Range' : 'Date Range'}
          </button>
          <button
            type="button"
            className="purchase-btn purchase-btn--secondary"
            onClick={loadSales}
            disabled={loading || servicesLoading || Boolean(rangeError)}
          >
            Refresh
          </button>
        </div>
      </div>

      {showDateFilters && (
        <div className="sales-date-bar">
          <div className="sales-date-bar__fields">
            <label className="receivables-date-field">
              <span>From</span>
              <input
                type="date"
                value={fromDate}
                max={toDate || todayYmd()}
                onChange={(e) => handleFromChange(e.target.value)}
              />
            </label>
            <label className="receivables-date-field">
              <span>To</span>
              <input
                type="date"
                value={toDate}
                min={fromDate || undefined}
                max={todayYmd()}
                onChange={(e) => handleToChange(e.target.value)}
              />
            </label>
          </div>
          <p className="sales-date-hint">Search other days — max 2 months.</p>
        </div>
      )}

      {(rangeError || error) && (
        <div className="receivables-alert" role="alert">
          {rangeError || error}
        </div>
      )}

      {servicesLoading || loading ? (
        <p className="receivables-empty">Loading sales…</p>
      ) : channels.length === 0 ? (
        <p className="receivables-empty">No sales channels enabled for this restaurant.</p>
      ) : (
        <>
          <section className="sales-section">
            <div className="sales-section__header">
              <h3 className="sales-section__title">
                {showTodaysInSection ? "Today's" : 'Summary'}
              </h3>
              <p className="sales-section__meta">{rangeLabel}</p>
            </div>
            <div className="purchase-stats-grid sales-stats-grid" aria-live="polite">
              {channels.map((channel) => {
                const meta = CHANNEL_META[channel.key];
                const Icon = meta.icon;
                const value = amounts[channel.key] ?? 0;
                return (
                  <div
                    key={channel.key}
                    className={`purchase-stat-card purchase-stat-card--summary sales-stat-card sales-stat-card--${meta.tone}`}
                  >
                    <div className="sales-stat-card__top">
                      <span className="sales-stat-icon-wrap" aria-hidden="true">
                        <Icon className="sales-stat-icon" />
                      </span>
                      <span className="sales-stat-label">{meta.label}</span>
                    </div>
                    <span className={`sales-stat-value purchase-stat-value--${meta.tone}`}>
                      {formatSettlementAmount(value)}
                    </span>
                  </div>
                );
              })}
              <div className="purchase-stat-card purchase-stat-card--summary sales-stat-card sales-stat-card--total">
                <div className="sales-stat-card__top">
                  <span className="sales-stat-icon-wrap" aria-hidden="true">
                    <FaChartPie className="sales-stat-icon" />
                  </span>
                  <span className="sales-stat-label">Total</span>
                </div>
                <span className="sales-stat-value">
                  {formatSettlementAmount(totalAmount)}
                </span>
              </div>
            </div>
          </section>

          <section className="sales-section sales-section--table">
            <div className="sales-section__header">
              <h3 className="sales-section__title">
                {showTodaysInSection ? 'Today By Channel' : 'Sales By Date'}
              </h3>
            </div>
            <div className="purchase-detail-lines-table-wrap">
              <table className="purchase-detail-lines-table receivables-table sales-by-date-table">
                <thead>
                  <tr>
                    <OrdersSortableTh
                      label="Date"
                      sortKey="date"
                      activeSortKey={tableSort?.key}
                      sortDirection={tableSort?.direction}
                      onSortAsc={handleSortAsc}
                      onSortDesc={handleSortDesc}
                    />
                    {channels.map((channel) => (
                      <OrdersSortableTh
                        key={channel.key}
                        label={CHANNEL_META[channel.key].label}
                        sortKey={`channel:${channel.key}`}
                        activeSortKey={tableSort?.key}
                        sortDirection={tableSort?.direction}
                        onSortAsc={handleSortAsc}
                        onSortDesc={handleSortDesc}
                        className="purchase-detail-lines-table__num"
                      />
                    ))}
                    <OrdersSortableTh
                      label="Total"
                      sortKey="total"
                      activeSortKey={tableSort?.key}
                      sortDirection={tableSort?.direction}
                      onSortAsc={handleSortAsc}
                      onSortDesc={handleSortDesc}
                      className="purchase-detail-lines-table__num"
                    />
                    <OrdersSortableTh
                      label="Orders"
                      sortKey="orderCount"
                      activeSortKey={tableSort?.key}
                      sortDirection={tableSort?.direction}
                      onSortAsc={handleSortAsc}
                      onSortDesc={handleSortDesc}
                      className="purchase-detail-lines-table__num"
                    />
                  </tr>
                </thead>
                <tbody>
                  {sortedDailyRows.length === 0 ? (
                    <tr>
                      <td colSpan={tableColSpan} className="receivables-empty-cell">
                        No sales in this date range.
                      </td>
                    </tr>
                  ) : (
                    sortedDailyRows.map((row) => (
                      <tr key={row.date}>
                        <td>{formatDisplayDate(row.date)}</td>
                        {channels.map((channel) => (
                          <td key={channel.key} className="purchase-detail-lines-table__num">
                            {formatSettlementAmount(row.amounts[channel.key] || 0)}
                          </td>
                        ))}
                        <td className="purchase-detail-lines-table__num">
                          {formatSettlementAmount(row.total)}
                        </td>
                        <td className="purchase-detail-lines-table__num">{row.orderCount}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default Sales;
